import type { PreviewReview, ReviewSourceId } from '@flexbyt/shared';
import { DEMO_REVIEWS } from './demo-reviews.js';
import { samplePhoto } from './sample-photos.js';

/**
 * A source's demo reviews with each reviewer's photograph in place of the
 * drawing. Applied here rather than in the list that shows them, so the list,
 * the widget previewed from it — inline and in the framed tablet and phone
 * views, which rebuild the set from its id — and the page's hero all show the
 * same face for the same person.
 */
function photographed(reviews: PreviewReview[] | undefined): PreviewReview[] {
  return (reviews ?? []).map((review) => ({
    ...review,
    authorAvatar: samplePhoto(review) ?? review.authorAvatar,
  }));
}

/**
 * Sample review sets for the free builder, by kind of business.
 *
 * ===========================================================================
 * Why the tool ships with a choice of these
 * ===========================================================================
 * The builder has to look convincing the second it loads, and "convincing"
 * depends entirely on who is looking. A hotel owner reading four reviews about
 * a boiler repair is looking at somebody else's business, and the tool stops
 * demonstrating anything about theirs. Letting them switch to hotel reviews in
 * one click costs a dropdown and makes the preview feel like it is about them.
 *
 * The reviews themselves come from `DEMO_REVIEWS`, which is already written per
 * source and per audience — so this is a grouping and a label, not a second
 * pile of invented content.
 *
 * ===========================================================================
 * What these are not
 * ===========================================================================
 * Not ReviewsJet testimonials, and never presented as any. They are the content
 * inside a demonstration, marked `isSample: true` the same way the dashboard
 * marks the sample set it shows a new account. ReviewsJet's own customer
 * reviews are a separate thing entirely and come from the API — see
 * `content/testimonials.ts`.
 *
 * They are also replaced the moment somebody connects a source. The builder
 * says so rather than leaving people to assume these travel with them.
 */

export type SampleSet = {
  id: string;
  /** What the visitor picks it by. */
  label: string;
  /** The kind of business it reads as, for the helper line. */
  audience: string;
  source: ReviewSourceId;
  reviews: PreviewReview[];
};

export const SAMPLE_SETS: SampleSet[] = [
  {
    id: 'local',
    label: 'Local business',
    audience: 'A trade, a shop, a service — reviews from Google',
    source: 'google',
    reviews: photographed(DEMO_REVIEWS.google),
  },
  {
    id: 'hotel',
    label: 'Hotel or B&B',
    audience: 'Guest reviews from Tripadvisor',
    source: 'tripadvisor',
    reviews: photographed(DEMO_REVIEWS.tripadvisor),
  },
  {
    id: 'restaurant',
    label: 'Restaurant or bar',
    audience: 'Reviews from Yelp',
    source: 'yelp',
    reviews: photographed(DEMO_REVIEWS.yelp),
  },
  {
    id: 'software',
    label: 'Software or SaaS',
    audience: 'Customer reviews from Trustpilot',
    source: 'trustpilot',
    reviews: photographed(DEMO_REVIEWS.trustpilot),
  },
  {
    id: 'rental',
    label: 'Holiday rental',
    audience: 'Guest reviews from Airbnb',
    source: 'airbnb',
    reviews: photographed(DEMO_REVIEWS.airbnb),
  },
  {
    id: 'android',
    label: 'Android app',
    audience: 'User reviews from Google Play',
    source: 'googleplay',
    reviews: photographed(DEMO_REVIEWS.googleplay),
  },
  {
    id: 'ios',
    label: 'iPhone app',
    audience: 'User reviews from the App Store',
    source: 'appstore',
    reviews: photographed(DEMO_REVIEWS.appstore),
  },

  /*
    The four the Chrome extension collects.

    Here for the same reason as the sets above — an Etsy seller is a kind of
    business and should see reviews that sound like their customers — and
    because the Etsy, Amazon, AliExpress and TourRadar pages otherwise fell back
    to the first set and told somebody "these are Etsy sample reviews" while
    showing Google-marked reviews of a plumber.
  */
  {
    id: 'etsy',
    label: 'Handmade or Etsy shop',
    audience: 'Buyer reviews collected from Etsy',
    source: 'etsy',
    reviews: photographed(DEMO_REVIEWS.etsy),
  },
  {
    id: 'amazon',
    label: 'Product brand',
    audience: 'Product reviews collected from Amazon',
    source: 'amazon',
    reviews: photographed(DEMO_REVIEWS.amazon),
  },
  {
    id: 'aliexpress',
    label: 'Online store',
    audience: 'Product reviews collected from AliExpress',
    source: 'aliexpress',
    reviews: photographed(DEMO_REVIEWS.aliexpress),
  },
  {
    id: 'tours',
    label: 'Tour operator',
    audience: 'Traveller reviews collected from TourRadar',
    source: 'tourradar',
    reviews: photographed(DEMO_REVIEWS.tourradar),
  },
];

/**
 * The set written for a given platform.
 *
 * Integration pages know their source, so they get its reviews rather than the
 * builder's default — a Tripadvisor page showing a plumber's Google reviews is
 * demonstrating somebody else's business.
 *
 * Falls back to the first set for the four extension sources, which have no set
 * of their own: their pages explain a mechanism rather than previewing a
 * platform's review style, and an empty widget would be worse than a generic
 * one. The page says the reviews are samples either way.
 */
export function sampleSetForSource(source: ReviewSourceId): SampleSet {
  return SAMPLE_SETS.find((set) => set.source === source) ?? SAMPLE_SETS[0]!;
}

export function sampleSetById(id: string): SampleSet {
  return SAMPLE_SETS.find((set) => set.id === id) ?? SAMPLE_SETS[0]!;
}

/**
 * The first `count` reviews of a set.
 *
 * Each set holds eight and the builder never asks for more, so in practice this
 * is a slice. The cycling branch is kept as a floor rather than a feature: if a
 * set is ever trimmed below what a control offers, a repeated review is a
 * better failure than an empty wall — but the counts are chosen so it does not
 * happen, because a visitor reads a duplicated card as a broken widget.
 *
 * Their real reviews replace all of this the moment a source is connected.
 */
export function fill(reviews: PreviewReview[], count: number): PreviewReview[] {
  if (reviews.length === 0) return [];

  const out: PreviewReview[] = [];
  for (let index = 0; index < count; index++) {
    const source = reviews[index % reviews.length]!;
    out.push(
      index < reviews.length ? source : { ...source, id: `${source.id}-${index}` }
    );
  }
  return out;
}
